// Logo — RubixEMS brand mark. Three exports per COMPONENTS §Catalog.
//
// SVG path data carried verbatim from dev's Logo.tsx (reference per
// COMPONENTS §Catalog) — the only primitive whose shape is reference-as-is.
// Switched dev's `var(--color-primary)` raw → `var(--color-primary-display)`
// per DESIGN-TOKENS §Dark mode (OKLCH-clamped for dark-mode brand legibility;
// UI everywhere reads --color-primary-display, never --color-primary raw).
//
// Structural strokes use `currentColor` so the parent's text-color class
// determines them (e.g. text-[var(--color-text-primary)]). Accents use the
// brand var; auto-flips per project + per mode at runtime via ThemeProvider.

export interface LogoMarkProps {
  size?: number;
  className?: string;
  'aria-hidden'?: boolean;
}

export interface LogoLockupProps {
  height?: number;
  className?: string;
}

export interface LogoStackedProps {
  height?: number;
  className?: string;
}

export interface LogoWordmarkProps {
  height?: number;
  className?: string;
}

function CubePaths() {
  return (
    <>
      {/* Face fills */}
      <path d="M16 4L27 10L16 16L5 10Z" fill="var(--color-primary-display)" fillOpacity={0.18} />
      <path d="M27 10L27 22L16 28L16 16Z" fill="currentColor" fillOpacity={0.1} />
      <path d="M5 10L16 16L16 28L5 22Z" fill="currentColor" fillOpacity={0.05} />

      {/* Outer hexagon */}
      <polygon
        points="16,4 27,10 27,22 16,28 5,22 5,10"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
      />

      {/* Inner Y */}
      <line x1={16} y1={16} x2={27} y2={10} stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" />
      <line x1={16} y1={16} x2={16} y2={28} stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" />
      <line x1={16} y1={16} x2={5} y2={10} stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" />

      {/* Top-face accent outline */}
      <path
        d="M16 4L27 10L16 16L5 10Z"
        fill="none"
        stroke="var(--color-primary-display)"
        strokeWidth={1.75}
        strokeLinejoin="round"
      />

      {/* Front-corner measurement node */}
      <circle cx={16} cy={16} r={1.6} fill="var(--color-primary-display)" />
    </>
  );
}

export function LogoMark({ size = 32, className, 'aria-hidden': ariaHidden }: LogoMarkProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      width={size}
      height={size}
      fill="none"
      className={className}
      role={ariaHidden ? undefined : 'img'}
      aria-label={ariaHidden ? undefined : 'RubixEMS'}
      aria-hidden={ariaHidden}
    >
      <CubePaths />
    </svg>
  );
}


// ── Wordmark glyphs — Inter outlines, converted to paths (ADR-031) ──────────
// "Rubix" at weight 300 / "EMS" at weight 700, size 15.5, tracking 0.5/1.2 —
// generated from the bundled Inter variable font (fontkit, build-time one-off)
// so the brand mark renders identically on every platform instead of in
// whatever system-ui resolves to. Total advance ≈ 80.23 units; baseline at
// y=0 (instances position via transform). The ink half carries a fill
// transition: inside a `group` link the whole wordmark unifies into the brand
// color on hover.
const WORDMARK_W = 80.23;
const RUBIX_D =
  'M1.49 0L1.49 -11.28L5.17 -11.28Q6.36 -11.28 7.17 -10.84Q7.98 -10.41 8.4 -9.64Q8.81 -8.88 8.81 -7.88Q8.81 -6.89 8.4 -6.14Q7.98 -5.38 7.18 -4.96Q6.37 -4.53 5.18 -4.53L2.09 -4.53L2.09 -5.5L5.13 -5.5Q6.01 -5.5 6.58 -5.8Q7.16 -6.09 7.45 -6.63Q7.74 -7.16 7.74 -7.88Q7.74 -8.62 7.45 -9.17Q7.16 -9.71 6.58 -10.01Q6 -10.32 5.12 -10.32L2.57 -10.32L2.57 0ZM8.04 0L5.3 -5.09L6.51 -5.09L9.28 0ZM14.52 0.11Q13.67 0.11 13.01 -0.25Q12.36 -0.61 11.99 -1.3Q11.62 -1.99 11.62 -2.99L11.62 -8.46L12.65 -8.46L12.65 -3.07Q12.65 -2.03 13.24 -1.43Q13.82 -0.82 14.79 -0.82Q15.46 -0.82 15.99 -1.11Q16.52 -1.39 16.83 -1.93Q17.13 -2.46 17.13 -3.19L17.13 -8.46L18.16 -8.46L18.16 0L17.16 0L17.16 -1.98L17.31 -1.98Q16.97 -0.86 16.22 -0.37Q15.47 0.11 14.52 0.11ZM24.96 0.18Q24.2 0.18 23.68 -0.09Q23.17 -0.37 22.85 -0.78Q22.54 -1.19 22.36 -1.58L22.25 -1.58L22.25 0L21.25 0L21.25 -11.28L22.28 -11.28L22.28 -6.83L22.36 -6.83Q22.53 -7.22 22.84 -7.62Q23.16 -8.02 23.67 -8.3Q24.18 -8.57 24.96 -8.57Q26 -8.57 26.78 -8.03Q27.55 -7.49 27.99 -6.5Q28.43 -5.52 28.43 -4.21Q28.43 -2.89 28 -1.9Q27.56 -0.92 26.78 -0.37Q26 0.18 24.96 0.18ZM24.82 -0.75Q25.66 -0.75 26.23 -1.21Q26.8 -1.67 27.1 -2.46Q27.4 -3.25 27.4 -4.22Q27.4 -5.18 27.1 -5.96Q26.81 -6.74 26.23 -7.19Q25.66 -7.64 24.82 -7.64Q24.01 -7.64 23.43 -7.21Q22.86 -6.77 22.56 -5.99Q22.26 -5.22 22.26 -4.22Q22.26 -3.21 22.57 -2.43Q22.87 -1.64 23.44 -1.2Q24.01 -0.75 24.82 -0.75ZM31.08 0L31.08 -8.46L32.11 -8.46L32.11 0ZM31.6 -10.03Q31.29 -10.03 31.07 -10.24Q30.85 -10.45 30.85 -10.75Q30.85 -11.05 31.07 -11.26Q31.29 -11.47 31.6 -11.47Q31.91 -11.47 32.13 -11.26Q32.35 -11.05 32.35 -10.75Q32.35 -10.45 32.13 -10.24Q31.91 -10.03 31.6 -10.03ZM34.38 0L37.71 -4.73L37.7 -3.95L34.55 -8.46L35.75 -8.46L37.04 -6.56Q37.41 -6.01 37.71 -5.51Q38.01 -5.01 38.31 -4.53L37.78 -4.53Q38.1 -5.01 38.37 -5.51Q38.64 -6.01 39.02 -6.56L40.33 -8.46L41.51 -8.46L38.35 -3.91L38.35 -4.66L41.65 0L40.45 0L38.94 -2.19Q38.58 -2.72 38.3 -3.2Q38.03 -3.67 37.73 -4.12L38.27 -4.12Q37.97 -3.67 37.72 -3.2Q37.46 -2.72 37.1 -2.19L35.58 0Z';
const EMS_D =
  'M43.65 0L43.65 -11.28L51.19 -11.28L51.19 -9.36L45.96 -9.36L45.96 -6.64L50.8 -6.64L50.8 -4.75L45.96 -4.75L45.96 -1.91L51.21 -1.91L51.21 0ZM54.26 0L54.26 -11.28L57.81 -11.28L59.64 -6.1Q59.75 -5.74 59.9 -5.16Q60.05 -4.59 60.2 -3.92Q60.35 -3.25 60.48 -2.63Q60.61 -2 60.7 -1.54L60.25 -1.54Q60.33 -1.99 60.47 -2.61Q60.6 -3.24 60.75 -3.91Q60.9 -4.57 61.05 -5.15Q61.2 -5.74 61.31 -6.1L63.11 -11.28L66.67 -11.28L66.67 0L64.34 0L64.34 -5.31Q64.34 -5.68 64.36 -6.24Q64.37 -6.79 64.39 -7.43Q64.41 -8.07 64.42 -8.71Q64.44 -9.36 64.44 -9.94L64.59 -9.94Q64.44 -9.31 64.27 -8.64Q64.1 -7.97 63.92 -7.34Q63.74 -6.71 63.58 -6.19Q63.42 -5.66 63.31 -5.31L61.44 0L59.49 0L57.59 -5.31Q57.48 -5.66 57.32 -6.18Q57.16 -6.7 56.98 -7.33Q56.81 -7.95 56.63 -8.62Q56.44 -9.29 56.28 -9.94L56.46 -9.94Q56.47 -9.39 56.48 -8.75Q56.5 -8.11 56.52 -7.46Q56.53 -6.81 56.55 -6.26Q56.56 -5.7 56.56 -5.31L56.56 0ZM74.04 0.17Q72.68 0.17 71.68 -0.25Q70.68 -0.67 70.12 -1.48Q69.56 -2.3 69.53 -3.5L71.78 -3.5Q71.82 -2.93 72.12 -2.55Q72.41 -2.16 72.9 -1.98Q73.4 -1.79 74.02 -1.79Q74.64 -1.79 75.09 -1.96Q75.55 -2.14 75.81 -2.47Q76.07 -2.79 76.07 -3.22Q76.07 -3.61 75.84 -3.87Q75.61 -4.13 75.17 -4.32Q74.74 -4.51 74.12 -4.65L72.88 -4.96Q71.45 -5.31 70.64 -6.06Q69.83 -6.8 69.83 -8.03Q69.83 -9.05 70.38 -9.82Q70.93 -10.58 71.89 -11Q72.84 -11.43 74.07 -11.43Q75.32 -11.43 76.25 -11Q77.18 -10.57 77.7 -9.81Q78.22 -9.05 78.24 -8.06L76.01 -8.06Q75.95 -8.74 75.43 -9.11Q74.91 -9.48 74.05 -9.48Q73.46 -9.48 73.05 -9.32Q72.63 -9.15 72.42 -8.85Q72.2 -8.56 72.2 -8.18Q72.2 -7.77 72.45 -7.49Q72.7 -7.21 73.12 -7.04Q73.53 -6.87 74 -6.76L75.02 -6.51Q75.73 -6.35 76.35 -6.08Q76.96 -5.8 77.42 -5.41Q77.88 -5.01 78.14 -4.47Q78.39 -3.92 78.39 -3.21Q78.39 -2.19 77.88 -1.43Q77.36 -0.67 76.39 -0.25Q75.41 0.17 74.04 0.17Z';

function WordmarkGlyphs() {
  return (
    <>
      <path
        d={RUBIX_D}
        fill="currentColor"
        className="transition-[fill] duration-base group-hover:fill-[var(--color-primary-display)]"
      />
      <path d={EMS_D} fill="var(--color-primary-display)" />
    </>
  );
}

export function LogoLockup({ height = 32, className }: LogoLockupProps) {
  // viewBox trimmed to the actual content (wordmark ends ~unit 122) — the old
  // 172 width carried ~50 units of trailing whitespace that pushed anything
  // placed after the lockup (e.g. the platform switch caret) visually far away.
  const width = Math.round((height * 130) / 32);
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 130 32"
      width={width}
      height={height}
      fill="none"
      role="img"
      aria-label="RubixEMS"
      className={className}
    >
      <CubePaths />
      <line
        x1={38}
        y1={7}
        x2={38}
        y2={25}
        stroke="currentColor"
        strokeWidth={0.75}
        strokeOpacity={0.25}
        strokeLinecap="round"
      />
      <g transform="translate(44, 21)">
        <WordmarkGlyphs />
      </g>
    </svg>
  );
}

export function LogoStacked({ height = 80, className }: LogoStackedProps) {
  const width = Math.round((height * 82) / 52);
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="-1 0 82 52"
      width={width}
      height={height}
      fill="none"
      role="img"
      aria-label="RubixEMS"
      className={className}
    >
      <g transform="translate(24, 0)">
        <CubePaths />
      </g>
      <g transform={`translate(${40 - WORDMARK_W / 2}, 50)`}>
        <WordmarkGlyphs />
      </g>
    </svg>
  );
}

// LogoWordmark — the "RubixEMS" wordmark WITHOUT the cube (same two-tone
// treatment as the lockup: "Rubix" in currentColor, "EMS" in the brand accent).
// Used where the cube is omitted but the brand should still read — e.g. the
// mobile platform context, where the hamburger owns the left slot.
export function LogoWordmark({ height = 20, className }: LogoWordmarkProps) {
  // viewBox trimmed to the wordmark's actual extent (~unit 80) so nothing
  // placed after it sits in dead space.
  const width = Math.round((height * 86) / 32);
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 86 32"
      width={width}
      height={height}
      fill="none"
      role="img"
      aria-label="RubixEMS"
      className={className}
    >
      <g transform="translate(0, 21)">
        <WordmarkGlyphs />
      </g>
    </svg>
  );
}
